import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GitMerge, Search, ArrowLeftRight, AlertTriangle, Loader, FileSignature, FileText, Check } from 'lucide-react';
import api from '../../services/api';
import EnterpriseModal from '../EnterpriseModal';
import { useToastContext } from '../../context/ToastContext';
import { pickPrimary } from '../../utils/duplicateDetection';

const nameOf = (s) => [s?.firstName, s?.middleName, s?.lastName].filter(Boolean).join(' ').trim() || '—';
const phoneOf = (s) => s?.phone || s?.parentPhone || s?.guardianMobile || '';
const dayOf = (v) => {
  if (!v) return '';
  const d = v?.seconds ? new Date(v.seconds * 1000) : new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
};
// The order the preview lists what moves.
const MOVE_KEYS = [
  'attendance', 'paperAttendance', 'sessionParticipants', 'groupStudents', 'sessionPlans', 'jibaya', 'payments',
  'sportCredits', 'notes', 'files', 'fitnessTests', 'clientIntakes', 'uniformHistory', 'weekConfirmations',
  'lessons', 'jibayaContacts', 'courseRegistrations',
  // learning centers (homework, checklists, surveys, meetings, Quran…)
  'homeworkSubmissions', 'itemStatuses', 'checklists', 'surveyResponses', 'surveyShareTokens', 'meetings', 'media',
  'hifzLogs', 'quranMemorized', 'quranGoals', 'quranPlans', 'quranHomework', 'onboardingSubmissions',
];
// Warnings that need an explicit "it is the same child" tick before merging.
const STRONG_WARNINGS = ['maybe_sibling', 'national_id_differs', 'birth_date_differs'];

/**
 * Merge two files of the same student (every learning center; sport first) — docs/habib-sports/OWNER-REQUESTS-2026-10-21-SPEC.md §D.
 *
 * Steps: pick the other file (search, or `candidates` from the duplicates
 * modal) → choose which file to keep (default: the one with a signed intake,
 * else the newest; swap with one tap) → dry-run preview of what moves, the
 * conflicts and the blanks it fills → confirm → POST /students/:keepId/merge.
 * The merged file is archived with a link to the kept one.
 *
 * Props: isOpen, onClose, student (the file the modal was opened from),
 * candidates? (other files to choose from), initialOtherId?, onMerged(keepId, fromId).
 */
export default function MergeClientsModal({ isOpen, onClose, student, candidates = null, initialOtherId = null, onMerged }) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'he';
  const { success, error: toastError } = useToastContext();

  const [other, setOther] = useState(null);
  const [keepId, setKeepId] = useState(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [sameChild, setSameChild] = useState(false);
  const [applying, setApplying] = useState(false);

  const pickOther = useCallback((o) => {
    setOther(o);
    setPreview(null);
    setSameChild(false);
    if (o && student) setKeepId(pickPrimary([student, o], { prefer: 'intake' }).id);
  }, [student]);

  // Fresh state on every open.
  useEffect(() => {
    if (!isOpen) return;
    setQuery(student?.lastName || '');
    setResults([]);
    setPreview(null);
    setPreviewError('');
    setApplying(false);
    const first = initialOtherId && candidates ? candidates.find((c) => c.id === initialOtherId) : null;
    if (first) pickOther(first);
    else { setOther(null); setKeepId(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Search the center's clients for the other file (when no candidates were given).
  useEffect(() => {
    if (!isOpen || other || candidates) return undefined;
    const term = query.trim();
    if (term.length < 2) { setResults([]); return undefined; }
    let cancelled = false;
    setSearching(true);
    const h = setTimeout(async () => {
      try {
        const res = await api.get('/students', { params: { search: term, limit: 15, enrich: 0 } });
        const list = Array.isArray(res) ? res : (res?.data || []);
        if (!cancelled) setResults(list.filter((s) => s.id !== student?.id && s.isActive !== false && !s.mergedInto));
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(h); };
  }, [query, isOpen, other, candidates, student?.id]);

  const keep = other && keepId ? (keepId === student?.id ? student : other) : null;
  const from = other && keepId ? (keepId === student?.id ? other : student) : null;

  // Dry run whenever the pair or the direction changes.
  useEffect(() => {
    if (!isOpen || !keep || !from) return undefined;
    let cancelled = false;
    setPreviewing(true);
    setPreviewError('');
    setPreview(null);
    setSameChild(false);
    api.post(`/students/${keep.id}/merge`, { fromId: from.id, dryRun: true })
      .then((res) => { if (!cancelled) setPreview(res?.data ?? res); })
      .catch((e) => { if (!cancelled) setPreviewError(e?.message || t('clientMerge.previewFailed', 'Could not load the preview')); })
      .finally(() => { if (!cancelled) setPreviewing(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, keep?.id, from?.id]);

  const warnings = preview?.warnings || [];
  const needsTick = warnings.some((w) => STRONG_WARNINGS.includes(w));
  const canApply = !!preview && !previewing && !applying && (!needsTick || sameChild);

  const apply = async () => {
    if (!canApply) return;
    setApplying(true);
    try {
      await api.post(`/students/${keep.id}/merge`, { fromId: from.id, dryRun: false });
      success(t('clientMerge.done', { name: nameOf(keep), defaultValue: 'Files merged into {{name}}' }));
      onMerged?.(keep.id, from.id);
      onClose?.();
    } catch (e) {
      toastError(e?.message || t('clientMerge.failed', 'The merge did not finish — try again to resume it'));
      setApplying(false);
    }
  };

  const moves = useMemo(() => MOVE_KEYS
    .map((k) => [k, Number(preview?.moves?.[k]) || 0])
    .filter(([, n]) => n > 0), [preview]);
  const fills = Object.entries(preview?.profileFill || {});
  const conflicts = preview?.conflicts || [];

  const fileCard = (s) => {
    const isKeep = s.id === keepId;
    return (
      <label
        key={s.id}
        data-testid={`merge-file-${s.id}`}
        style={{
          flex: '1 1 220px', minWidth: 0, display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer',
          padding: '0.75rem', borderRadius: 12,
          border: `2px solid ${isKeep ? 'var(--color-success, #16a34a)' : 'var(--color-border)'}`,
          background: isKeep ? 'var(--color-success-light, #dcfce7)' : 'var(--color-surface)',
        }}
      >
        <input type="radio" name="merge-keep" checked={isKeep} onChange={() => setKeepId(s.id)} data-testid={`merge-keep-${s.id}`} style={{ marginTop: 3 }} />
        <span style={{ minWidth: 0, flex: 1 }}>
          <span style={{ display: 'block', fontWeight: 600, overflowWrap: 'anywhere' }}>{nameOf(s)}</span>
          {phoneOf(s) && <span dir="ltr" style={{ display: 'block', fontSize: '0.75rem', color: 'var(--color-text-tertiary)', textAlign: isRTL ? 'right' : 'left' }}>{phoneOf(s)}</span>}
          <span style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
            {s.intakeId && <span style={chip('#15803d', '#dcfce7')}><FileSignature size={11} /> {t('clientMerge.signedIntake', 'Signed intake')}</span>}
            {s.source === 'paper_import' && <span style={chip('#92400e', '#fef3c7')}><FileText size={11} /> {t('clientMerge.paperFile', 'Paper file')}</span>}
            {dayOf(s.createdAt) && <span style={chip('var(--color-text-secondary)', 'var(--color-background)')}>{t('clientMerge.createdOn', { date: dayOf(s.createdAt), defaultValue: 'Created {{date}}' })}</span>}
          </span>
          <span style={{ display: 'block', marginTop: 6, fontSize: '0.75rem', fontWeight: 700, color: isKeep ? 'var(--color-success-dark, #15803d)' : 'var(--color-text-tertiary)' }}>
            {isKeep ? `✓ ${t('clientMerge.keep', 'Keep this file')}` : t('clientMerge.mergeIn', 'Merged in and archived')}
          </span>
        </span>
      </label>
    );
  };

  const footer = (
    <div style={{ display: 'flex', gap: 8, width: '100%', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
      {other && !candidates && (
        <button type="button" onClick={() => pickOther(null)} style={ghostBtn} disabled={applying}>
          {t('clientMerge.back', 'Choose another file')}
        </button>
      )}
      <button type="button" onClick={onClose} style={ghostBtn} disabled={applying}>{t('common.cancel', 'Cancel')}</button>
      <button
        type="button"
        onClick={apply}
        disabled={!canApply}
        data-testid="merge-apply"
        style={{ ...primaryBtn, opacity: canApply ? 1 : 0.5, cursor: canApply ? 'pointer' : 'not-allowed' }}
      >
        {applying ? <Loader size={16} style={{ animation: 'mergeSpin 1s linear infinite' }} /> : <GitMerge size={16} />}
        {applying ? t('clientMerge.applying', 'Merging…') : t('clientMerge.apply', 'Merge files')}
      </button>
    </div>
  );

  return (
    <EnterpriseModal
      isOpen={isOpen}
      onClose={applying ? () => {} : onClose}
      size="lg"
      title={t('clientMerge.title', 'Merge with another file')}
      subtitle={t('clientMerge.subtitle', 'The same child twice: keep one file and bring everything of the other into it')}
      titleIcon={<GitMerge size={18} />}
      footer={footer}
      dataTestId="merge-clients-modal"
    >
      <style>{'@keyframes mergeSpin { to { transform: rotate(360deg); } }'}</style>
      <div dir={isRTL ? 'rtl' : 'ltr'} style={{ display: 'grid', gap: 14 }}>
        {!other && (
          <div style={{ display: 'grid', gap: 8 }}>
            <div style={{ fontWeight: 600 }}>{t('clientMerge.pickOther', { name: nameOf(student), defaultValue: 'Which other file is {{name}}?' })}</div>
            {!candidates && (
              <div style={{ position: 'relative' }}>
                <Search size={16} style={{ position: 'absolute', top: 12, insetInlineStart: 10, color: 'var(--color-text-tertiary)' }} />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t('clientMerge.searchPlaceholder', 'Search by name or phone')}
                  data-testid="merge-search"
                  style={{ width: '100%', boxSizing: 'border-box', padding: '0.625rem 0.75rem', paddingInlineStart: 34, borderRadius: 10, border: '1px solid var(--color-border)', fontSize: 16 }}
                />
              </div>
            )}
            {searching && <div style={{ color: 'var(--color-text-tertiary)', fontSize: '0.8125rem' }}>{t('clientMerge.searching', 'Searching…')}</div>}
            <div style={{ display: 'grid', gap: 6 }}>
              {(candidates || results).filter((c) => c.id !== student?.id).map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => pickOther(c)}
                  data-testid={`merge-pick-${c.id}`}
                  style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', minHeight: 44, padding: '0.5rem 0.75rem', borderRadius: 10, border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text-primary)', cursor: 'pointer', textAlign: 'start' }}
                >
                  <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{nameOf(c)}</span>
                  {phoneOf(c) && <span dir="ltr" style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)' }}>{phoneOf(c)}</span>}
                </button>
              ))}
              {!candidates && !searching && query.trim().length >= 2 && results.length === 0 && (
                <div style={{ color: 'var(--color-text-tertiary)', fontSize: '0.8125rem' }}>{t('clientMerge.noResults', 'No other file found')}</div>
              )}
            </div>
          </div>
        )}

        {other && keepId && (
          <>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'stretch' }}>
              {fileCard(student)}
              {fileCard(other)}
            </div>
            <button
              type="button"
              onClick={() => setKeepId(keepId === student.id ? other.id : student.id)}
              disabled={applying}
              data-testid="merge-swap"
              style={{ ...ghostBtn, justifySelf: 'start' }}
            >
              <ArrowLeftRight size={14} /> {t('clientMerge.swap', 'Keep the other file instead')}
            </button>

            {previewing && (
              <div style={{ color: 'var(--color-text-tertiary)', display: 'flex', gap: 8, alignItems: 'center' }}>
                <Loader size={16} style={{ animation: 'mergeSpin 1s linear infinite' }} /> {t('clientMerge.loadingPreview', 'Checking what will move…')}
              </div>
            )}
            {previewError && <div role="alert" style={{ color: 'var(--color-danger, #dc2626)' }}>{previewError}</div>}

            {preview && (
              <div style={{ display: 'grid', gap: 12 }} data-testid="merge-preview">
                {warnings.length > 0 && (
                  <div role="alert" style={{ display: 'grid', gap: 6, padding: '0.625rem 0.75rem', borderRadius: 10, background: 'var(--color-warning-light, #fef3c7)', color: 'var(--color-warning-dark, #92400e)', fontSize: '0.8125rem' }}>
                    {warnings.map((w) => (
                      <div key={w} style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }} data-testid={`merge-warning-${w}`}>
                        <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                        <span>{t(`clientMerge.warnings.${w}`, w)}</span>
                      </div>
                    ))}
                    {needsTick && (
                      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 600, minHeight: 32 }}>
                        <input type="checkbox" checked={sameChild} onChange={(e) => setSameChild(e.target.checked)} data-testid="merge-same-child" />
                        {t('clientMerge.confirmSame', 'I checked — this is the same child')}
                      </label>
                    )}
                  </div>
                )}

                <section>
                  <div style={sectionTitle}>{t('clientMerge.movesTitle', { name: nameOf(keep), defaultValue: 'Moves to {{name}}' })}</div>
                  {moves.length === 0 ? (
                    <div style={{ color: 'var(--color-text-tertiary)', fontSize: '0.8125rem' }}>{t('clientMerge.nothingMoves', 'Nothing to move — only the old file is archived')}</div>
                  ) : (
                    <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {moves.map(([k, n]) => (
                        <li key={k} data-testid={`merge-move-${k}`} style={chip('var(--color-primary-dark)', 'var(--color-primary-light)')}>
                          {t(`clientMerge.moves.${k}`, k)}: <b>{n}</b>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                {conflicts.length > 0 && (
                  <section>
                    <div style={sectionTitle}>{t('clientMerge.conflictsTitle', { count: conflicts.length, defaultValue: 'In both files ({{count}}) — resolved like this' })}</div>
                    <ul style={{ margin: 0, paddingInlineStart: 18, fontSize: '0.8125rem', display: 'grid', gap: 2 }}>
                      {conflicts.map((c, i) => (
                        <li key={`${c.type}-${c.lessonId || c.groupId || c.planId || c.weekKey}-${i}`}>
                          {t(`clientMerge.conflicts.${c.type}.${c.resolution}`, `${c.type}: ${c.resolution}`)}
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {fills.length > 0 && (
                  <section>
                    <div style={sectionTitle}>{t('clientMerge.fillTitle', 'Filled in on the kept file (it was empty)')}</div>
                    <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 2, fontSize: '0.8125rem' }}>
                      {fills.map(([k, v]) => (
                        <li key={k} data-testid={`merge-fill-${k}`} style={{ display: 'flex', gap: 6, minWidth: 0 }}>
                          <Check size={14} style={{ flexShrink: 0, marginTop: 2, color: 'var(--color-success, #16a34a)' }} />
                          <span style={{ fontWeight: 600 }}>{t(`clientMerge.fields.${k}`, k)}:</span>
                          <span style={{ overflowWrap: 'anywhere', minWidth: 0 }}>{typeof v === 'object' ? '✓' : String(v)}</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)' }}>
                  {t('clientMerge.archiveNote', { name: nameOf(from), defaultValue: '{{name}} is archived (not deleted) and opening it shows a link to the kept file.' })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </EnterpriseModal>
  );
}

const chip = (color, background) => ({
  display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', fontWeight: 600,
  color, background, borderRadius: 999, padding: '0.15rem 0.55rem',
});
const sectionTitle = { fontWeight: 700, fontSize: '0.875rem', marginBottom: 6 };
const ghostBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 44, padding: '0.5rem 0.9rem', borderRadius: 10,
  border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text-primary)', cursor: 'pointer', fontSize: '0.875rem',
};
const primaryBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 44, padding: '0.5rem 1.1rem', borderRadius: 10,
  border: 'none', background: 'var(--color-primary, #2563eb)', color: 'var(--color-on-primary, #fff)', fontWeight: 600, fontSize: '0.875rem',
};
